import React, { useState, useEffect, useCallback } from 'react';
import s from './CollageSchemes.module.css';
import { pythonBackAxiosInstance } from "../../../api";

interface ICollageSchemesProps {
    gender: string;
    styleName: string;
    requestId: string;
    onSchemeChange?: (index: number, schemaId: number | null) => void;
    savedSchemaIds?: (number | null)[];
    capsuleCount?: number;
}

interface ICollageSchemesByGender {
    id: number;
    name: string;
    gender: string;
    album_photo_link: string;
}

const CollageSchemes: React.FC<ICollageSchemesProps> = ({
                                                            gender,
                                                            requestId,
                                                            onSchemeChange,
                                                            savedSchemaIds = [],
                                                            capsuleCount = 5
                                                        }) => {
    const [allSchemes, setAllSchemes] = useState<ICollageSchemesByGender[]>([]);
    const [selectedSchemes, setSelectedSchemes] = useState<(number | null)[]>(Array(capsuleCount).fill(null));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);

    const getSchemesByGender = useCallback(async () => {
        try {
            const response = await pythonBackAxiosInstance.get<ICollageSchemesByGender[]>('/v1/schemas_by_gender', {
                params: {
                    gender: gender
                }
            });
            setAllSchemes(response.data);
        } catch (error) {
            setAllSchemes([]);
        }
    }, [gender]);

    useEffect(() => {
        setSelectedSchemes(prev => {
            const newArray = Array(capsuleCount).fill(null);
            for (let i = 0; i < Math.min(prev.length, capsuleCount); i++) {
                newArray[i] = prev[i];
            }
            return newArray;
        });
    }, [capsuleCount]);

    useEffect(() => {
        if (savedSchemaIds.length > 0) {
            setSelectedSchemes(prev => {
                const newArray = Array(Math.max(capsuleCount, savedSchemaIds.length)).fill(null);
                for (let i = 0; i < savedSchemaIds.length; i++) {
                    newArray[i] = savedSchemaIds[i];
                }
                return newArray;
            });
        }
    }, [savedSchemaIds, capsuleCount]);

    useEffect(() => {
        getSchemesByGender();
    }, [getSchemesByGender]);

    const handleSelectScheme = (slotIndex: number) => {
        setSelectedSlotIndex(slotIndex);
        setIsModalOpen(true);
    };

    const handleSchemeSelect = (selectedScheme: ICollageSchemesByGender) => {
        if (selectedSlotIndex === null) return;

        const updatedSchemes = [...selectedSchemes];
        updatedSchemes[selectedSlotIndex] = selectedScheme.id;
        setSelectedSchemes(updatedSchemes);

        if (onSchemeChange) {
            onSchemeChange(selectedSlotIndex, selectedScheme.id);
        }

        setIsModalOpen(false);
        setSelectedSlotIndex(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedSlotIndex(null);
    };

    const handleRemoveScheme = (slotIndex: number) => {
        const updatedSchemes = [...selectedSchemes];
        updatedSchemes[slotIndex] = null;
        setSelectedSchemes(updatedSchemes);

        if (onSchemeChange) {
            onSchemeChange(slotIndex, null);
        }
    };

    const getSchemeById = (schemaId: number | null) => {
        if (!schemaId) return null;
        return allSchemes.find(scheme => scheme.id === schemaId);
    };

    const renderSlots = Array.from({ length: capsuleCount }, (_, index) => {
        const schemaId = selectedSchemes[index] || null;
        const schema = getSchemeById(schemaId);

        return (
            <div key={index} className={s.schemeItem}>
                <div className={s.schemeHeader}>
                    <span className={s.schemeNumber}>Коллаж {index + 1}</span>
                </div>
                {schema ? (
                    <div className={s.schemeImageContainer}>
                        <img
                            src={schema.album_photo_link}
                            alt={schema.name}
                            className={s.schemeImage}
                        />
                        <div className={s.schemeOverlay}>
                            <button
                                className={s.changeButton}
                                onClick={() => handleSelectScheme(index)}
                            >
                                Изменить
                            </button>
                            <button
                                className={s.deleteButton}
                                onClick={() => handleRemoveScheme(index)}
                            >
                                Удалить
                            </button>
                        </div>
                        <div className={s.schemeNameLabel}>
                            {schema.name}
                        </div>
                    </div>
                ) : (
                    <div className={s.emptySlot}>
                        <button
                            className={s.selectButton}
                            onClick={() => handleSelectScheme(index)}
                        >
                            Выбрать схему
                        </button>
                    </div>
                )}
            </div>
        );
    });

    return (
        <div className={s.container}>
            <div className={s.schemesCount}>
                <span className={s.countLabel}>
                    Выбрано схем: {selectedSchemes.filter(id => id !== null).length} из {capsuleCount}
                </span>
            </div>
            <div className={s.schemesSlider}>
                {renderSlots}
            </div>

            {isModalOpen && (
                <div className={s.modalOverlay}>
                    <div className={s.modal}>
                        <div className={s.modalHeader}>
                            <h2>
                                Выберите схему для {selectedSlotIndex !== null ? `Коллажа ${selectedSlotIndex + 1}` : ''}
                            </h2>
                            <button
                                className={s.closeButton}
                                onClick={handleCloseModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={s.modalContent}>
                            {allSchemes.length === 0 ? (
                                <div className={s.emptyState}>
                                    <p>Нет доступных схем для {gender === 'female' ? 'женского' : 'мужского'} пола</p>
                                </div>
                            ) : (
                                allSchemes.map((schema) => {
                                    const isSelected = selectedSchemes.includes(schema.id);
                                    return (
                                        <div
                                            key={schema.id}
                                            className={`${s.modalSchemeItem} ${isSelected ? s.selected : ''}`}
                                            onClick={() => handleSchemeSelect(schema)}
                                        >
                                            <img
                                                src={schema.album_photo_link}
                                                alt={schema.name}
                                                className={s.modalSchemeImage}
                                            />
                                            <div className={s.modalSchemeInfo}>
                                                <h3>{schema.name}</h3>
                                            </div>
                                            {isSelected && (
                                                <div className={s.selectedBadge}>✓ Выбрана</div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollageSchemes;